/**
 * Hand-written application types.
 *
 * `src/types/database.ts` will be generated from the live schema by
 * `npm run db:types` once the local stack runs, and the row shapes here should be
 * replaced by the generated ones at that point. These exist so the UI can be built
 * and type-checked before the database is reachable; they mirror the columns in
 * docs/01-architecture.md §4 exactly.
 */

export const USER_ROLES = [
  'sales',
  'team_leader',
  'sales_manager',
  'marketing',
  'finance',
  'admin',
  'ceo',
] as const;

export type UserRole = (typeof USER_ROLES)[number];

export type RecordStatus = 'active' | 'archived';

export function isUserRole(value: unknown): value is UserRole {
  return typeof value === 'string' && (USER_ROLES as readonly string[]).includes(value);
}

/** The subset of `profiles` the app shell needs. Locked columns are read-only here. */
export interface Profile {
  id: string;
  full_name: string;
  email: string;
  phone: string | null;
  avatar_url: string | null;
  role: UserRole;
  team_id: string | null;
  manager_id: string | null;
  is_active: boolean;
  accepts_new_leads: boolean;
  capacity_max: number | null;
  areas: string[];
  languages: string[];
  status: RecordStatus;
  last_login_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface Team {
  id: string;
  name: string;
  leader_id: string | null;
  areas: string[];
  status: RecordStatus;
  created_at: string;
  updated_at: string;
}

// ---------------------------------------------------------------- Phase 2

export const LEAD_STAGES = [
  'new',
  'contacted',
  'qualified',
  'interested',
  'follow_up',
  'meeting',
  'site_visit',
  'offer',
  'negotiation',
  'reservation',
  'contract',
  'won',
  'lost',
] as const;
export type LeadStage = (typeof LEAD_STAGES)[number];

/** Stages a lead can still move through. `won` and `lost` are terminal. */
export const ACTIVE_STAGES = LEAD_STAGES.filter(
  (s) => s !== 'won' && s !== 'lost',
) as readonly LeadStage[];

export const LEAD_SOURCES = [
  'meta',
  'google',
  'tiktok',
  'website',
  'walk_in',
  'referral',
  'call_in',
  'broker',
  'other',
] as const;
export type LeadSource = (typeof LEAD_SOURCES)[number];

export const CLIENT_TYPES = ['end_user', 'investor', 'corporate', 'broker'] as const;
export type ClientType = (typeof CLIENT_TYPES)[number];

export const TEMPERATURES = ['hot', 'warm', 'cold'] as const;
export type Temperature = (typeof TEMPERATURES)[number];

export const LEAD_HEALTH = ['active', 'at_risk', 'dormant', 'dead'] as const;
export type LeadHealth = (typeof LEAD_HEALTH)[number];

export const SLA_STATUSES = ['pending', 'met', 'breached'] as const;
export type SlaStatus = (typeof SLA_STATUSES)[number];

export const PAYMENT_METHODS = ['cash', 'installments'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

export const PURCHASE_HORIZONS = ['immediate', 'within_3m', 'within_6m', 'beyond_6m'] as const;
export type PurchaseHorizon = (typeof PURCHASE_HORIZONS)[number];

export const UNIT_TYPES = [
  'apartment',
  'villa',
  'townhouse',
  'twinhouse',
  'duplex',
  'studio',
  'chalet',
  'office',
  'retail',
  'clinic',
] as const;
export type UnitType = (typeof UNIT_TYPES)[number];

export const ACTIVITY_TYPES = [
  'lead_created',
  'assigned',
  'reassigned',
  'opened',
  'call',
  'whatsapp',
  'note',
  'stage_changed',
  'meeting',
  'site_visit',
  'offer',
  'reservation',
  'contract',
  'sla_breach',
  'archived',
  'restored',
  'duplicate',
] as const;
export type ActivityType = (typeof ACTIVITY_TYPES)[number];

/** Activity types a rep logs by hand, as opposed to ones the system writes. */
export const LOGGABLE_ACTIVITY_TYPES = [
  'call',
  'whatsapp',
  'note',
  'meeting',
  'site_visit',
] as const satisfies readonly ActivityType[];

export const CALL_OUTCOMES = [
  'answered',
  'no_answer',
  'busy',
  'wrong_number',
  'invalid',
  'switched_off',
] as const;
export type CallOutcome = (typeof CALL_OUTCOMES)[number];

export interface Lead {
  id: string;
  full_name: string;
  phone_primary: string;
  whatsapp_phone: string | null;
  email: string | null;
  client_type: ClientType | null;
  temperature: Temperature | null;
  budget_min: string | null;
  budget_max: string | null;
  preferred_areas: string[];
  unit_types: UnitType[];
  bedrooms: number | null;
  payment_method: PaymentMethod | null;
  down_payment_capacity: string | null;
  purchase_horizon: PurchaseHorizon | null;
  source: LeadSource | null;
  captured_at: string;
  owner_id: string | null;
  team_id: string | null;
  stage: LeadStage;
  stage_entered_at: string;
  lost_reason: string | null;
  sla_due_at: string | null;
  first_contact_at: string | null;
  sla_status: SlaStatus;
  next_action_type: string | null;
  next_action_at: string | null;
  next_action_note: string | null;
  last_activity_at: string | null;
  is_overdue: boolean;
  health: LeadHealth;
  count_calls: number;
  count_whatsapp: number;
  count_notes: number;
  count_meetings: number;
  count_visits: number;
  count_offers: number;
  duplicate_hits: number;
  status: RecordStatus;
  created_at: string;
  updated_at: string;
}

export interface LeadActivity {
  id: number;
  occurred_at: string;
  lead_id: string;
  type: ActivityType;
  actor_id: string | null;
  actor_name?: string | null;
  outcome: CallOutcome | null;
  duration_seconds: number | null;
  body: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
}

/** The single-round-trip payload from customer_360(). */
export interface Customer360 {
  lead: Lead;
  owner: { id: string; full_name: string; email: string } | null;
  team: { id: string; name: string } | null;
  timeline: LeadActivity[];
  counts: {
    calls: number;
    whatsapp: number;
    notes: number;
    meetings: number;
    visits: number;
    offers: number;
    duplicates: number;
  };
}
